import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckIcon, ArrowLeftIcon, ArrowRightIcon, PlayIcon } from "@heroicons/react/24/outline";
import { Button } from "../../ui";
import VaptToolSelector from "../components/VaptToolSelector";
import {
  listVaptProjectsApi,
  createVaptScanApi,
  launchVaptScanApi,
  listVaptTargetsApi,
  ensureVaptAssetEngagementApi,
  listVaptScanTemplatesApi,
  validateVaptTargetApi,
} from "../../../services/axios/AuthApis/VAPTApis";
import { VAPT_DOMAINS } from "../utils/vaptConstants";
import { getVaptErrorMessage } from "../utils/apiError";

const STEPS = ["Project", "Domain", "Assets", "Validation", "Template", "Tools", "Config", "Review"];

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

const VaptCreateScan = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedProjectId = searchParams.get("project");
  const [step, setStep] = useState(preselectedProjectId ? 1 : 0);
  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [projectId, setProjectId] = useState(preselectedProjectId || "");
  const [domain, setDomain] = useState("");
  const [assetIds, setAssetIds] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [tools, setTools] = useState([]);
  const [scanName, setScanName] = useState("");
  const [notes, setNotes] = useState("");
  const [templates, setTemplates] = useState([]);

  // Real VAPTTarget rows for the selected project, only fetched/used when
  // the project is asset-sourced (project.source === "ASSET_MANAGER"). Asset
  // VAPT targets already carry a real `domain` (derived server-side from
  // AssetVAPTService.classify_cloud_asset_for_vapt when the project was
  // created from Asset Manager) — so for this path the domain step should
  // not force a manual, disconnected dropdown; it derives from real target
  // data instead.
  const [assetTargets, setAssetTargets] = useState([]);
  const [assetTargetsLoading, setAssetTargetsLoading] = useState(false);
  // Bumped after an explicit, user-initiated "Sync Assets" to reload targets.
  const [reloadTick, setReloadTick] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    listVaptProjectsApi()
      .then((data) => setProjects(asArray(data)))
      .catch((err) => setError(getVaptErrorMessage(err, "Failed to load projects.")));
    listVaptScanTemplatesApi()
      .then((data) => setTemplates(asArray(data)))
      .catch((err) => setError(getVaptErrorMessage(err, "Failed to load scan templates.")));
  }, []);

  const selectedProject = useMemo(() => projects.find((p) => p.project_id === projectId), [projects, projectId]);
  const isAssetSourced = selectedProject?.source === "ASSET_MANAGER";

  // Loads REAL VAPTTarget rows for the selected project, regardless of
  // engagement_type. The targets/scope/authorization LIST endpoints filter
  // by the project's business key (`project_id`, e.g. "VAPT-3199BDC6"),
  // while WRITE payloads reference the project's Mongo `id`. Passing the
  // Mongo id here matched nothing and made every project look empty.
  //
  // Opening the wizard is read-only: it never triggers an Asset Manager
  // sync. When an asset-sourced engagement has no targets, the user is
  // offered an explicit "Sync Assets" action instead.
  useEffect(() => {
    if (!selectedProject?.project_id) {
      setAssetTargets([]);
      return;
    }
    let mounted = true;
    setAssetTargetsLoading(true);
    listVaptTargetsApi({ project_id: selectedProject.project_id })
      .then((rows) => {
        if (mounted) setAssetTargets(asArray(rows));
      })
      .catch((err) => {
        if (mounted) {
          setAssetTargets([]);
          setError(getVaptErrorMessage(err, "Failed to load targets for this project."));
        }
      })
      .finally(() => {
        if (mounted) setAssetTargetsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedProject?.project_id, reloadTick]);

  const syncAssets = async () => {
    if (!selectedProject?.customer_id) return;
    setSyncing(true);
    setError(null);
    try {
      await ensureVaptAssetEngagementApi(selectedProject.customer_id);
      setReloadTick((n) => n + 1);
    } catch (err) {
      setError(getVaptErrorMessage(err, "Asset sync failed."));
    } finally {
      setSyncing(false);
    }
  };

  const assetDomains = useMemo(
    () => Array.from(new Set(assetTargets.map((t) => t.domain).filter(Boolean))),
    [assetTargets]
  );

  // For a project (asset- or external-sourced) whose real targets are all
  // in exactly one domain, lock the domain automatically instead of making
  // the user pick from the full generic domain list - e.g. HabileSec
  // Website VAPT's single https://www.demo.com target auto-detects
  // as Web the moment its project is selected.
  useEffect(() => {
    if (assetDomains.length === 1 && domain !== assetDomains[0]) {
      setDomain(assetDomains[0]);
    }
  }, [assetDomains, domain]);

  // Real per-target validation checklist (Authorization / In-Scope /
  // Domain Supported / Target Reachable / SSL-TLS / Testing Window /
  // Eligible) - POST /api/vapt/targets/<id>/validate/, the same real,
  // non-fabricated checks vapt/target_validation.py runs. Keyed by target
  // id; refetched whenever the selected targets change.
  const [validationResults, setValidationResults] = useState({});
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    if (assetIds.length === 0) return;
    let mounted = true;
    setValidating(true);
    Promise.all(
      assetIds.map((id) =>
        validateVaptTargetApi(id)
          .then((data) => [id, data])
          .catch((err) => [id, err?.response?.data || { valid: false, checks: {}, message: "Validation request failed." }])
      )
    ).then((pairs) => {
      if (!mounted) return;
      setValidationResults(Object.fromEntries(pairs));
      setValidating(false);
    });
    return () => {
      mounted = false;
    };
  }, [assetIds]);

  const validationRows = useMemo(() => {
    return assetIds.map((id) => {
      const target = assetTargets.find((t) => t.id === id);
      const result = validationResults[id];
      return {
        id,
        name: target?.asset_display_name || target?.external_value || target?.target_type || id,
        checks: result?.checks || null,
        eligible: !!result?.valid,
        state: target?.status || "Unknown",
      };
    });
  }, [assetIds, assetTargets, validationResults]);

  const hasBlockedAssets = validationRows.some((r) => !r.eligible);

  // Real vapt.models.ScanConfiguration rows (GET /api/vapt/templates/) -
  // never a hardcoded frontend template list. A template not tied to a
  // specific domain (scan_type doesn't map 1:1 to a VAPT domain for every
  // template) is still offered; ones whose scanning_tools are entirely
  // outside the current domain's real registry are filtered out below via
  // VaptToolSelector's own domain filter once selected.
  const applicableTemplates = templates;

  const selectTemplate = (t) => {
    setTemplateId(t.config_id);
    // scanning_tools already holds real tool_ids (see seed_vapt_templates),
    // the same values VaptToolSelector/the backend orchestration expect.
    setTools(t.scanning_tools || []);
  };

  const canProceed = () => {
    switch (step) {
      case 0:
        return !!projectId;
      case 1:
        return !!domain;
      case 2:
        return assetIds.length > 0;
      case 3:
        return assetIds.length > 0 && !hasBlockedAssets;
      case 4:
        return !!templateId;
      case 5:
        return tools.length > 0;
      default:
        return true;
    }
  };

  const submit = async () => {
    const project = projects.find((p) => p.project_id === projectId);
    setSubmitting(true);
    setError(null);
    try {
      if (!project?.id) {
        throw new Error("Selected project could not be resolved — please reselect it.");
      }
      // The scan must reference the real VAPTTarget the user selected in
      // the Assets step (its `id` is what the backend serializer resolves
      // to a display value and what the executors scope findings to).
      // Never fall back to a "unspecified" placeholder when a real,
      // authorized target exists.
      const selectedTargetId =
        assetIds[0] || assetTargets.find((t) => t.domain === domain)?.id || null;
      if (!selectedTargetId) {
        throw new Error(
          "Select at least one authorized target for this domain before creating the scan.",
        );
      }
      // SIMPLIFICATION: this creates only the VulnerabilityScan record for
      // the selected project/domain/tools. It does not also create a
      // VAPTTarget per selected Asset Manager asset (assetIds) — doing that
      // would require mapping each mock asset into a real
      // POST /api/vapt/targets/ call, which is out of scope for this pass.
      // The selected asset count is still shown to the user in the Review
      // step so the simplification is visible, not silently dropped.
      //
      // VulnerabilityScan.project is a mongoengine ReferenceField, so the
      // real Mongo `id` (not the human-readable project_id) must be sent.
      const scan = await createVaptScanApi({
        project: project.id,
        scan_name: scanName || `${domain} scan for ${project?.name || projectId}`,
        scan_type: domain,
        configured_tools: tools,
        target_id: selectedTargetId,
      });
      // Creating the VulnerabilityScan record alone leaves it sitting at its
      // default status="Queued" without ever entering the real Celery task
      // chain (create_scan_task -> assign_worker_task -> execute_tool_task
      // -> finalize_scan_task). The "Launch Scan" button must actually
      // trigger that — POST /api/vapt/scans/{scan_id}/launch/. The backend
      // can legitimately 400 this (e.g. external targets lacking an
      // Approved Authorization); when that happens the scan record still
      // exists, so we navigate to it and let VaptScanDetail.js show its
      // real (unlaunched) status rather than pretending success.
      try {
        await launchVaptScanApi(scan.scan_id);
      } catch (launchErr) {
        const launchMsg = getVaptErrorMessage(launchErr, "Scan was created but could not be launched.");
        navigate(`/ai-vapt/scans/${scan.scan_id}`, { state: { launchError: launchMsg } });
        return;
      }
      navigate(`/ai-vapt/scans/${scan.scan_id}`);
    } catch (err) {
      setError(getVaptErrorMessage(err, "Failed to create scan."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeftIcon className="w-4 h-4" /> Back
      </button>

      <div>
        <h2 className="text-lg font-bold text-gray-900">New Scan</h2>
        <p className="text-sm text-gray-500">Configure and launch a new VAPT scan</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5">{error}</div>
      )}

      {/* Stepper */}
      <div className="flex items-center overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center text-center w-24 flex-shrink-0">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                  i < step
                    ? "bg-primary-600 border-primary-600 text-white"
                    : i === step
                    ? "border-primary-600 text-primary-600"
                    : "border-gray-300 text-gray-400"
                }`}
              >
                {i < step ? <CheckIcon className="w-4 h-4" /> : i + 1}
              </div>
              <p className={`text-[11px] mt-1 font-medium ${i <= step ? "text-gray-800" : "text-gray-400"}`}>{s}</p>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 -mt-4 ${i < step ? "bg-primary-600" : "bg-gray-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 min-h-[320px]">
        {step === 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800">Select Project</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {projects.map((p) => (
                <div
                  key={p.project_id}
                  onClick={() => setProjectId(p.project_id)}
                  className={`border rounded-lg p-3 cursor-pointer ${
                    projectId === p.project_id ? "border-primary-500 bg-primary-50" : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900">{p.name}</p>
                  <p className="text-xs text-gray-500">{p.client_name || "—"}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800">Select Domain</h3>
            {assetTargetsLoading && (
              <p className="text-xs text-gray-400">Loading target domains for this engagement…</p>
            )}
            {!assetTargetsLoading && assetDomains.length === 1 && (
              <p className="text-xs text-green-700">
                All targets for this engagement are in the <strong>{assetDomains[0]}</strong> domain — domain selected
                automatically.
              </p>
            )}
            {isAssetSourced && !assetTargetsLoading && assetDomains.length === 0 && (
              <div className="text-xs text-amber-600 space-y-2">
                <p>
                  No VAPT targets exist for this engagement yet. If the customer has assets in Asset Manager, sync them
                  to create the targets.
                </p>
                <Button variant="secondary" onClick={syncAssets} disabled={syncing}>
                  {syncing ? "Syncing…" : "Sync Assets"}
                </Button>
              </div>
            )}
            {!isAssetSourced && !assetTargetsLoading && assetDomains.length === 0 && (
              <p className="text-xs text-amber-600">
                This engagement has no targets — add one under the Scope &amp; Targets tab first.
              </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(assetDomains.length > 0 ? assetDomains : VAPT_DOMAINS).map((d) => (
                <div
                  key={d}
                  onClick={() => {
                    setDomain(d);
                    setTools([]);
                    setTemplateId("");
                  }}
                  className={`border rounded-lg p-3 text-center cursor-pointer ${
                    domain === d ? "border-primary-500 bg-primary-50" : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900">{d}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800">Select Targets ({domain})</h3>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-72 overflow-y-auto">
              <p className="text-[11px] text-gray-400 px-1 pb-1">
                {isAssetSourced
                  ? "Targets synchronized from Asset Manager for this engagement."
                  : "Targets added to this External VAPT engagement (add more from the Scope & Targets tab)."}
              </p>
              {assetTargets.filter((t) => t.domain === domain).length === 0 && (
                <p className="p-4 text-sm text-gray-400">No targets in this domain for this engagement.</p>
              )}
              {assetTargets
                .filter((t) => t.domain === domain)
                .map((t) => {
                  const checked = assetIds.includes(t.id);
                  const authorized = ["Authorized", "Ready for Scan", "Validated"].includes(t.status);
                  return (
                    <div
                      key={t.id}
                      onClick={() =>
                        authorized &&
                        setAssetIds((prev) => (checked ? prev.filter((i) => i !== t.id) : [...prev, t.id]))
                      }
                      className={`flex items-center justify-between px-4 py-2.5 ${
                        authorized ? "cursor-pointer hover:bg-gray-50" : "opacity-50 cursor-not-allowed"
                      } ${checked ? "bg-primary-50" : ""}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {t.asset_display_name || t.external_value || t.target_type || "Target"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t.target_type || "—"} • {t.status}
                          {!authorized && " (not authorized — add/confirm it under Scope & Targets first)"}
                        </p>
                      </div>
                      <div
                        className={`w-4 h-4 rounded flex items-center justify-center border flex-shrink-0 ${
                          checked ? "bg-primary-600 border-primary-600" : "border-gray-300"
                        }`}
                      >
                        {checked && <CheckIcon className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800">Target Validation</h3>
            <p className="text-xs text-gray-400">
              Pre-scan validation per selected target — ineligible targets block scan creation.
            </p>
            {validating && <p className="text-xs text-gray-400">Validating…</p>}
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
              {validationRows.map((r) => {
                const CHECK_LABELS = [
                  ["authorization", "Authorization"],
                  ["in_scope", "In-Scope"],
                  ["domain_supported", "Domain Supported"],
                  ["target_reachable", "Target Reachable"],
                  ["ssl_tls", "SSL/TLS"],
                  ["testing_window", "Testing Window"],
                ];
                return (
                  <div key={r.id} className="px-4 py-3 text-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-900">{r.name}</p>
                        <p className="text-[11px] text-gray-400">{r.state}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`font-semibold text-xs ${r.eligible ? "text-green-700" : "text-red-700"}`}>
                          {r.eligible ? "Eligible" : "Blocked"}
                        </span>
                        {!r.eligible && (
                          <button
                            type="button"
                            onClick={() => setAssetIds((prev) => prev.filter((id) => id !== r.id))}
                            className="text-[11px] text-primary-600 underline"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    {r.checks && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                        {CHECK_LABELS.map(([key, label]) => {
                          const c = r.checks[key];
                          if (!c) return null;
                          const isNA = c.passed === null;
                          return (
                            <span
                              key={key}
                              title={c.detail}
                              className={isNA ? "text-gray-400" : c.passed ? "text-green-700" : "text-red-700"}
                            >
                              {label} {isNA ? "N/A" : c.passed ? "✓" : "✗"}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {hasBlockedAssets && (
              <p className="text-xs text-red-700">
                One or more selected targets are blocked. Remove them or go back and reselect eligible targets to proceed.
              </p>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800">Select Template</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {applicableTemplates.length === 0 && (
                <p className="text-xs text-gray-400 col-span-full">
                  No scan templates are available for this domain. Skip this step and select tools directly in the next step.
                </p>
              )}
              {applicableTemplates.map((t) => (
                <div
                  key={t.config_id}
                  onClick={() => selectTemplate(t)}
                  className={`border rounded-lg p-3 cursor-pointer ${
                    templateId === t.config_id ? "border-primary-500 bg-primary-50" : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900">{t.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800">Select Tools</h3>
            <VaptToolSelector domain={domain} selectedTools={tools} onChange={setTools} />
          </div>
        )}

        {step === 6 && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-800">Configuration</h3>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Scan Name</label>
              <input
                value={scanName}
                onChange={(e) => setScanName(e.target.value)}
                placeholder={`${domain} scan`}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                placeholder="Any special configuration notes for this scan…"
              />
            </div>
          </div>
        )}

        {step === 7 && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-800">Review & Launch</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-400 uppercase">Project</p>
                <p className="text-gray-800">{projects.find((p) => p.project_id === projectId)?.name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Domain</p>
                <p className="text-gray-800">{domain}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Assets</p>
                <p className="text-gray-800">{assetIds.length} selected</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Template</p>
                <p className="text-gray-800">{templates.find((t) => t.config_id === templateId)?.name || "—"}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-400 uppercase">Tools</p>
                <p className="text-gray-800">{tools.join(", ")}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-400 uppercase">Scan Name</p>
                <p className="text-gray-800">{scanName || `${domain} scan`}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="secondary" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
          Previous
        </Button>
        {step < STEPS.length - 1 ? (
          <Button icon={ArrowRightIcon} iconPosition="right" disabled={!canProceed()} onClick={() => setStep((s) => s + 1)}>
            Next
          </Button>
        ) : (
          <Button icon={PlayIcon} loading={submitting} onClick={submit}>
            Launch Scan
          </Button>
        )}
      </div>
    </div>
  );
};

export default VaptCreateScan;
